import { useCallback, useEffect, useState } from 'react'
import type { StoredFile } from '../lib/api'
import { formatBytes, previewKind, type PreviewKind } from '../lib/format'
import { Spinner } from './ui'

/** Rendered thumbnail width; also what the PDF page is rasterised at. */
const THUMB_W = 320
/** Higher-resolution render for the lightbox. */
const FULL_W = 1100

interface Props {
  files: StoredFile[]
  /** Builds the download URL; `inline` asks S3 for a renderable copy. */
  urlFor: (fileId: string, inline: boolean) => string
  /** Builds the server-rendered thumbnail URL. */
  thumbFor: (fileId: string) => string
  readOnly: boolean
  onRemove: (file: StoredFile) => void
  /** Called while any thumbnail is still rendering, to refetch the file list. */
  onAwaitingThumb?: () => void
}

/**
 * Uploaded images and PDFs as thumbnail cards rather than filename rows.
 *
 * Every card uses a thumbnail the server rendered once and stored in S3. The
 * browser used to download the full original and let CSS shrink it, which meant
 * a 12 MP photo cost several megabytes to draw a 320px card; PDFs cost a
 * 1.6 MB pdf.js download plus a render, per viewer. Now both fetch a few
 * kilobytes.
 *
 * pdf.js survives for one job only: the lightbox, where a 640px thumbnail
 * upscaled would be unreadable and the page needs rendering at full size. That
 * download now happens when someone opens a PDF, not when a page listing one
 * loads.
 */
export function PreviewGrid({
  files,
  urlFor,
  thumbFor,
  readOnly,
  onRemove,
  onAwaitingThumb,
}: Props) {
  const [lightbox, setLightbox] = useState<StoredFile | null>(null)

  // Renders finish a second or two after the upload, so poll until they land
  // rather than leaving a spinner on screen until the next natural refetch.
  const waiting = files.some(
    (f) => f.thumbStatus === 'pending' || f.thumbStatus === 'rendering',
  )
  useEffect(() => {
    if (!waiting || !onAwaitingThumb) return
    const id = setInterval(onAwaitingThumb, 2000)
    return () => clearInterval(id)
  }, [waiting, onAwaitingThumb])

  return (
    <>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {files.map((file) => {
          const kind = previewKind(file.contentType, file.filename)
          if (!kind) return null
          return (
            <li key={file.id} className="group relative">
              <button
                type="button"
                onClick={() => setLightbox(file)}
                className="block w-full overflow-hidden rounded-xl border border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-950"
                aria-label={`Preview ${file.filename}`}
              >
                <Thumb
                  kind={kind}
                  file={file}
                  thumbSrc={thumbFor(file.id)}
                  inlineSrc={urlFor(file.id, true)}
                />
              </button>

              <div className="mt-1.5 flex items-baseline gap-2">
                <span className="min-w-0 flex-1 truncate text-xs font-medium" title={file.filename}>
                  {file.filename}
                </span>
                <span className="shrink-0 text-xs text-zinc-500">{formatBytes(file.sizeBytes)}</span>
              </div>

              <div className="mt-0.5 flex gap-2 text-xs">
                <a href={urlFor(file.id, false)} className="font-semibold text-brand-600 hover:underline">
                  Download
                </a>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => onRemove(file)}
                    data-destructive
                    className="font-semibold text-zinc-400 hover:text-red-600"
                  >
                    Remove
                  </button>
                )}
              </div>
            </li>
          )
        })}
      </ul>

      {lightbox && (
        <Lightbox
          file={lightbox}
          kind={previewKind(lightbox.contentType, lightbox.filename)!}
          inlineUrl={urlFor(lightbox.id, true)}
          downloadUrl={urlFor(lightbox.id, false)}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  )
}

function Thumb({
  kind,
  file,
  thumbSrc,
  inlineSrc,
}: {
  kind: PreviewKind
  file: StoredFile
  /** Server-rendered preview, used for images. */
  thumbSrc: string
  /** The original, used by pdf.js which has to read the document itself. */
  inlineSrc: string
}) {
  switch (file.thumbStatus) {
    case 'ready':
      return <ImageThumb src={thumbSrc} alt={file.filename} />
    case 'pending':
    case 'rendering':
      return (
        <Placeholder>
          <Spinner className="size-3" /> Preparing preview…
        </Placeholder>
      )
    default:
      // 'failed' or 'none'. For a PDF there is still a client-side path, so use
      // it rather than showing nothing — this is what a deployment without
      // pdftoppm falls back to.
      if (kind === 'pdf') {
        return <PdfThumb src={inlineSrc} alt={file.filename} width={THUMB_W} />
      }
      return <Placeholder>No preview</Placeholder>
  }
}

function Placeholder({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex aspect-4/3 items-center justify-center gap-2 text-xs text-zinc-400">
      {children}
    </div>
  )
}

function ImageThumb({ src, alt }: { src: string; alt: string }) {
  const [failed, setFailed] = useState(false)
  if (failed) return <Placeholder>Preview unavailable</Placeholder>
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className="aspect-4/3 w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
    />
  )
}

/** First page of a PDF, rasterised in the browser. */
function PdfThumb({
  src,
  alt,
  width,
  contain = false,
}: {
  src: string
  alt: string
  width: number
  contain?: boolean
}) {
  const [state, setState] = useState<{ url?: string; pages?: number; error?: boolean }>({})

  useEffect(() => {
    let cancelled = false
    setState({})
    // Dynamic import: pdf.js never reaches the initial bundle.
    import('../lib/pdf')
      .then(({ renderFirstPage }) => renderFirstPage(src, width))
      .then(({ dataUrl, pageCount }) => {
        if (!cancelled) setState({ url: dataUrl, pages: pageCount })
      })
      .catch((err) => {
        console.error('pdf preview', err)
        if (!cancelled) setState({ error: true })
      })
    return () => {
      cancelled = true
    }
  }, [src, width])

  if (state.error) return <Placeholder>PDF preview unavailable</Placeholder>
  if (!state.url)
    return (
      <Placeholder>
        <Spinner className="size-3" /> Rendering PDF…
      </Placeholder>
    )

  return (
    <div className={contain ? '' : 'relative'}>
      <img
        src={state.url}
        alt={alt}
        className={
          contain
            ? 'max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl'
            : 'aspect-4/3 w-full bg-white object-cover object-top transition-transform duration-200 group-hover:scale-[1.03]'
        }
      />
      {!contain && state.pages !== undefined && state.pages > 1 && (
        <span className="absolute right-1.5 bottom-1.5 rounded bg-black/65 px-1.5 py-0.5 text-[10px] font-semibold text-white">
          {state.pages} pages
        </span>
      )}
    </div>
  )
}

function Lightbox({
  file,
  kind,
  inlineUrl,
  downloadUrl,
  onClose,
}: {
  file: StoredFile
  kind: PreviewKind
  inlineUrl: string
  downloadUrl: string
  onClose: () => void
}) {
  const onKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose],
  )

  useEffect(() => {
    document.addEventListener('keydown', onKey)
    // Stop the page behind the overlay from scrolling with it.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [onKey])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={file.filename}
      onClick={onClose}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
    >
      <div onClick={(e) => e.stopPropagation()} className="max-w-full">
        {kind === 'pdf' ? (
          <PdfThumb src={inlineUrl} alt={file.filename} width={FULL_W} contain />
        ) : (
          <img
            src={inlineUrl}
            alt={file.filename}
            className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl"
          />
        )}
      </div>

      <div
        onClick={(e) => e.stopPropagation()}
        className="mt-4 flex flex-wrap items-center justify-center gap-4 text-sm text-white"
      >
        <span className="max-w-xs truncate font-medium">{file.filename}</span>
        <span className="text-white/60">{formatBytes(file.sizeBytes)}</span>
        {kind === 'pdf' && (
          // Only the first page is rendered here. Opening the whole document
          // means the browser's own viewer, so it stays an explicit choice.
          <a
            href={inlineUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold underline underline-offset-2"
          >
            Open all pages
          </a>
        )}
        <a href={downloadUrl} className="font-semibold underline underline-offset-2">
          Download
        </a>
        <button type="button" onClick={onClose} className="font-semibold underline underline-offset-2">
          Close
        </button>
      </div>
    </div>
  )
}
